import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';
import { cn } from '../../styles/lib/utils';

// The disabled: classes only ever match the button shape — an anchor has no disabled attribute.
// pointer-events-none rather than a disabled hover colour, so a consumer className that recolours
// the link is not overridden on hover by a colour pinned here.
const textLinkVariants = cva(
  'font-noto font-normal cursor-pointer disabled:pointer-events-none disabled:opacity-50 rounded-[2px] focus-ring',
  {
    variants: {
      variant: {
        default: 'text-xs leading-snug text-blue-50 hover:text-blue-60',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

type TextLinkVariantProps = VariantProps<typeof textLinkVariants>;

/**
 * Text styled as an inline link. Renders an anchor when `href` is set and a
 * button when it isn't, so an in-page action never submits a
 * surrounding form or leaves a nowhere-pointing `<a>` behind.
 */
type TextLinkProps =
  | ({ href: string } & React.ComponentProps<'a'> & TextLinkVariantProps)
  | ({ href?: undefined } & Omit<React.ComponentProps<'button'>, 'type'> & TextLinkVariantProps);

function TextLink(props: TextLinkProps) {
  if (props.href !== undefined) {
    const { className, variant, children, target, rel, ...anchorProps } = props;

    return (
      <a
        data-slot="text-link"
        {...anchorProps}
        target={target}
        // Opening a new tab hands the opener to the destination unless this is set.
        rel={target === '_blank' ? (rel ?? 'noopener noreferrer') : rel}
        className={cn(textLinkVariants({ variant }), className)}
      >
        {children}
      </a>
    );
  }

  const { className, variant, children, ...buttonProps } = props;

  return (
    <button
      data-slot="text-link"
      type="button"
      {...buttonProps}
      className={cn(textLinkVariants({ variant }), className)}
    >
      {children}
    </button>
  );
}

export { TextLink, textLinkVariants };
